import { useGetLeagueQuery } from "../api/endpoints/leagueDetail";
import { useGetDashboardQuery } from "../api/endpoints/dashboard";
import type { Clubhouse_LeagueOperatorDashboardLeagueDto } from "../types/leagues";

/**
 * Resolve a league's home facility id.
 *
 * The dashboard spans facilities, so a selected league may not belong to the
 * operator's currently-selected facility — every facility-scoped call for that
 * league must use the league's own facility. The detail response is the ideal
 * source, but it does not yet return facilityId, so we fall back to the
 * operator dashboard (which lists every league with its facilityId). The
 * dashboard resolves the id even on a direct URL / refresh, since RTK Query
 * fetches it if it isn't already cached.
 *
 * TODO: once GET /leagues/{id}/detail returns facilityId, the dashboard
 * fallback can be removed and this can read the detail response alone.
 */
export function useLeagueFacilityId(leagueId?: string): string | undefined {
  const { data: detail } = useGetLeagueQuery(leagueId ?? "", {
    skip: !leagueId,
  });
  const { data: dashboard } = useGetDashboardQuery();

  // `||` (not `??`) so an empty-string facilityId from detail still falls back.
  return (
    detail?.leagueV2Detail?.facilityId ||
    dashboard?.leagues?.find((l) => String(l.leagueId) === leagueId)
      ?.facilityId ||
    undefined
  );
}

/**
 * The dashboard row for this league, when the operator's list already has it.
 *
 * Detail is the source of truth once it loads. Until then (and when it fails)
 * the list still knows the name, status, and dates, so the page does not have
 * to fall back to a blank "League".
 */
export function useListedLeague(
  leagueId?: string,
): Clubhouse_LeagueOperatorDashboardLeagueDto | undefined {
  const { data: dashboard } = useGetDashboardQuery(undefined, {
    skip: !leagueId,
  });
  if (!leagueId) return undefined;
  return dashboard?.leagues?.find((l) => String(l.leagueId) === leagueId);
}
